/**
 * ダッシュボードの小さなグラフ(折れ線・棒)。外部ライブラリは使わず SVG で描く。
 * 色は tokens.css のグラフ用の色(2026-09-18 D-90): 系列は並び順に --chart-1〜6、目標の線は灰色の点線(--chart-target)、
 * グリッド線 --chart-grid、軸の文字 --chart-axis。置いた場所の幅を測って描くので、文字が伸び縮みしない。
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

export type Series = {
  key: string;
  label: string;
  /** 線・棒の色。省略すると並び順に --chart-1, --chart-2 …(目標の線は灰色。目標が2本あるときは色を渡して見分ける。2026-10-17) */
  color?: string;
  /** 'target' = 目標の線(灰色の点線・点なし) */
  kind?: 'value' | 'target';
  /** labels と同じ長さ。null はその日にデータが無い */
  values: (number | null)[];
};

type Props = {
  labels: string[];
  series: Series[];
  /** 'line' = 折れ線 / 'bar' = 棒(series は1本だけ使う) */
  type?: 'line' | 'bar';
  /** 値の表示(凡例と目盛り) */
  format?: (v: number) => string;
  /** x軸ラベルを何個おきに出すか(既定は自動) */
  labelEvery?: number;
  height?: number;
  /** 棒ごとの色(RANK の構成など) */
  barColors?: string[];
  /** 棒の上に値を出す */
  showValues?: boolean;
  /** 読み上げ用の説明 */
  ariaLabel?: string;
};

/** 目盛りの上限を切りのいい数にする */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const digits = Math.floor(Math.log10(v));
  const unit = Math.pow(10, digits);
  return Math.ceil(v / unit) * unit;
}

const PAD = { top: 16, right: 12, bottom: 24, left: 56 };

export default function MiniChart({ labels, series, type = 'line', format = (v) => String(v), labelEvery, height = 180, barColors, showValues = false, ariaLabel }: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(560);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    // 置いた場所の幅に合わせる。まず今の幅で描き(これが無いと 560 のまま縮小されて文字が小さくなる)、あとは変化のたびに測り直す
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const W = Math.max(240, width);
  const H = height;
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const max = niceMax(Math.max(0, ...all));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const n = labels.length;
  const slot = innerW / Math.max(1, n);
  const x = (i: number) => (type === 'bar' ? PAD.left + slot * (i + 0.5) : n <= 1 ? PAD.left + innerW / 2 : PAD.left + (innerW * i) / (n - 1));
  const y = (v: number) => PAD.top + innerH - (innerH * v) / max;
  const every = labelEvery ?? Math.max(1, Math.ceil(n / Math.max(4, Math.floor(innerW / 56))));

  let valueIndex = 0;
  const colorOf = new Map<string, string>();
  for (const s of series) {
    if (s.kind === 'target') colorOf.set(s.key, s.color ?? 'var(--chart-target)');
    else colorOf.set(s.key, s.color ?? `var(--chart-${(valueIndex++ % 6) + 1})`);
  }

  const empty = n === 0 || all.length === 0;
  let body: ReactNode = null;
  if (!empty) {
    body = (
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="ui-chart-svg" role="img" aria-label={ariaLabel}>
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(max * t)} y2={y(max * t)} className="ch-grid" />
            <text x={PAD.left - 8} y={y(max * t) + 4} className="ch-axis" textAnchor="end">
              {t === 0 ? '0' : format(max * t)}
            </text>
          </g>
        ))}

        {type === 'bar'
          ? (series[0]?.values ?? []).map((v, i) => {
              if (v === null) return null;
              const bw = Math.max(6, Math.min(48, slot * 0.6));
              const color = barColors?.[i] ?? colorOf.get(series[0]!.key)!;
              return (
                <g key={i}>
                  <rect x={x(i) - bw / 2} y={y(v)} width={bw} height={Math.max(0, y(0) - y(v))} fill={color} rx={2} />
                  {showValues && (
                    <text x={x(i)} y={y(v) - 4} className="ch-value" textAnchor="middle">
                      {format(v)}
                    </text>
                  )}
                </g>
              );
            })
          : series.map((s) => {
              const color = colorOf.get(s.key)!;
              const isTarget = s.kind === 'target';
              const pts = s.values.map((v, i) => (v === null ? null : `${x(i)},${y(v)}`)).filter((p): p is string => p !== null);
              return (
                <g key={s.key}>
                  <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={isTarget ? 1.5 : 2} strokeLinejoin="round" strokeLinecap="round" className={isTarget ? 'ch-target' : undefined} />
                  {!isTarget && n <= 31 && s.values.map((v, i) => (v === null ? null : <circle key={i} cx={x(i)} cy={y(v)} r={3} fill={color} />))}
                </g>
              );
            })}

        {labels.map((l, i) =>
          i % every === 0 || (type !== 'bar' && i === n - 1) ? (
            <text key={l + i} x={x(i)} y={H - 6} className="ch-axis" textAnchor="middle">
              {l}
            </text>
          ) : null,
        )}
      </svg>
    );
  }

  return (
    <div className="ui-chart" ref={boxRef}>
      {empty ? (
        <div className="ui-empty">データがありません</div>
      ) : (
        <>
          {body}
          {type !== 'bar' && (
            <div className="ch-legend">
              {series.map((s) => {
                const last = [...s.values].reverse().find((v): v is number => v !== null);
                return (
                  <span key={s.key} className="ch-legend-item">
                    <i className={`ch-swatch${s.kind === 'target' ? ' is-target' : ''}`} style={{ borderColor: colorOf.get(s.key) }} />
                    {s.label}
                    {last !== undefined && <b>{format(last)}</b>}
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
